import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock,
  MapPin,
  Printer,
  Receipt,
  TicketCheck,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { adminApi } from "../../../api/client";
import IssueTicketsDialog from "../../../components/admin/IssueTicketsDialog";
import LoadingState from "../../../components/common/LoadingState";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { useToast } from "../../../context/ToastContext";
import type { EventDashboard } from "../../../types/api";
import {
  formatCompactDate,
  formatCurrency,
  formatDateTime,
  formatEventDate,
} from "../../../utils/format";

const paymentBadge: Record<string, string> = {
  paid: "ego-badge ego-badge-success",
  successful: "ego-badge ego-badge-success",
  pending: "ego-badge ego-badge-warning",
  failed: "ego-badge ego-badge-danger",
  refunded: "ego-badge ego-badge-neutral",
};

const eventStatusBadge: Record<string, string> = {
  draft: "ego-badge ego-badge-neutral",
  published: "ego-badge ego-badge-success",
  "sold-out": "ego-badge ego-badge-warning",
  completed: "ego-badge ego-badge-info",
};

const chartColors = {
  gold: "#f5b303",
  charcoal: "#0b1220",
  blue: "#2563eb",
  green: "#16a34a",
  red: "#dc2626",
  slate: "#64748b",
  amber: "#d97706",
  purple: "#7c3aed",
};

const orderStatusColors: Record<string, string> = {
  paid: chartColors.green,
  pending: chartColors.amber,
  failed: chartColors.red,
  refunded: chartColors.slate,
};

const ticketStatusColors: Record<string, string> = {
  valid: chartColors.gold,
  "checked-in": chartColors.green,
  cancelled: chartColors.red,
};

function paymentBadgeFor(status?: string) {
  if (!status) return "ego-badge ego-badge-neutral";
  return paymentBadge[status.toLowerCase()] ?? "ego-badge ego-badge-neutral";
}

function eventBadgeFor(status?: string) {
  if (!status) return "ego-badge ego-badge-neutral";
  return eventStatusBadge[status.toLowerCase()] ?? "ego-badge ego-badge-neutral";
}

export default function AdminEventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { admin, token } = useAdminAuth();
  const { showToast } = useToast();
  const [data, setData] = useState<EventDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isIssueOpen, setIsIssueOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token || !id) return;
    try {
      const response = await adminApi.eventDashboard(token, id);
      setData(response);
      setError(null);
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Could not load screening";
      setError(message);
      showToast(message, "error");
    }
  }, [id, showToast, token]);

  useEffect(() => {
    if (!token || !id) return;
    void load();
    const intervalId = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(intervalId);
  }, [id, load, token]);

  if (!data && !error) {
    return <LoadingState label="Loading screening dashboard..." />;
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <Link
          to="/admin/screenings"
          className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> Back to screenings
        </Link>
        <div className="ego-surface px-6 py-12 text-center text-sm font-semibold text-red-600">
          {error ?? "Screening not found"}
        </div>
      </div>
    );
  }

  const {
    event,
    stats,
    tierStats,
    recentOrders,
    salesByDay,
    orderStatusBreakdown,
    ticketStatusBreakdown,
  } = data;
  const peakDay = salesByDay.reduce(
    (best, day) => (day.revenue > best.revenue ? day : best),
    { date: "", count: 0, revenue: 0 }
  );
  const tierChartData = tierStats.map((tier) => ({
    ...tier,
    sellThrough: tier.quantity > 0 ? Math.round((tier.sold / tier.quantity) * 100) : 0,
  }));
  const orderStatusTotal = orderStatusBreakdown.reduce((sum, item) => sum + item.count, 0);
  const ticketStatusTotal = ticketStatusBreakdown.reduce((sum, item) => sum + item.count, 0);
  const canIssueTickets = Boolean(admin?.effectivePermissions?.includes("tickets:issue"));
  const canViewOrders = Boolean(admin?.effectivePermissions?.includes("orders:view"));

  const kpis = [
    {
      label: "Gross revenue",
      value: formatCurrency(stats.grossRevenue),
      icon: Wallet,
      tint: "bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]",
    },
    {
      label: "Tickets sold",
      value: String(stats.totalSold),
      icon: TicketCheck,
      tint: "bg-[#eff6ff] dark:bg-[rgba(59,130,246,0.14)] text-[#1d4ed8]",
    },
    {
      label: "Tickets left",
      value: String(stats.available),
      icon: Users,
      tint: "bg-[#f5f3ff] dark:bg-[rgba(139,92,246,0.14)] text-[#6d28d9]",
    },
    {
      label: "Checked in",
      value: String(stats.checkedIn),
      icon: CheckCircle2,
      tint: "bg-[#ecfdf5] dark:bg-[rgba(16,185,129,0.14)] text-[#047857]",
    },
    {
      label: "Sell-through",
      value: `${stats.sellThrough}%`,
      icon: TrendingUp,
      tint: "bg-[#fffbeb] dark:bg-[rgba(245,158,11,0.14)] text-[#b45309]",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/admin/screenings"
          className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> Back to screenings
        </Link>
        {canIssueTickets && (
          <button type="button" onClick={() => setIsIssueOpen(true)} className="ego-btn-primary">
            <Printer size={15} /> Issue tickets
          </button>
        )}
      </div>

      <div className="ego-surface overflow-hidden">
        <div className="grid gap-0 lg:grid-cols-[280px_1fr]">
          <div className="relative h-48 overflow-hidden lg:h-full">
            <img src={event.image} alt={event.title} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-black/0" />
            {event.featured && (
              <span className="ego-badge ego-badge-brand absolute left-4 top-4">Featured</span>
            )}
          </div>
          <div className="p-6 lg:p-7">
            <div className="flex flex-wrap items-center gap-2">
              <span className="ego-badge ego-badge-brand">{event.category}</span>
              <span className={eventBadgeFor(event.status)}>{event.status}</span>
            </div>
            <h1 className="mt-3 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              {event.title}
            </h1>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays size={14} /> {formatEventDate(event.date, event.time)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={14} /> {event.venue}, {event.city}
              </span>
              {event.doorsOpen && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={14} /> Doors {event.doorsOpen}
                </span>
              )}
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Capacity utilization</span>
                <span className="font-semibold text-foreground">
                  {stats.totalSold} / {stats.capacity}
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-muted">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[var(--pp-gold)] to-[#ffb084]"
                  style={{ width: `${Math.min(stats.sellThrough, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} className="ego-surface p-5">
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${kpi.tint}`}>
                <Icon size={18} />
              </div>
              <div className="mt-5 text-2xl font-bold tracking-tight text-foreground">{kpi.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{kpi.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.35fr_0.65fr]">
        <ChartCard
          title="Sales trend"
          subtitle="Paid revenue and tickets sold by purchase day"
          isEmpty={salesByDay.length === 0}
        >
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={salesByDay} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.18)" />
              <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="revenue"
                tickFormatter={(value) => compactNumber(Number(value))}
                tick={{ fontSize: 12 }}
                width={42}
              />
              <YAxis
                yAxisId="tickets"
                orientation="right"
                tickFormatter={(value) => compactNumber(Number(value))}
                tick={{ fontSize: 12 }}
                width={32}
              />
              <Tooltip
                formatter={eventChartTooltipFormatter}
                labelFormatter={(label) => formatCompactDate(String(label))}
              />
              <Legend />
              <Bar yAxisId="revenue" dataKey="revenue" name="Revenue" fill={chartColors.gold} radius={[6, 6, 0, 0]} />
              <Line
                yAxisId="tickets"
                type="monotone"
                dataKey="count"
                name="Tickets sold"
                stroke={chartColors.blue}
                strokeWidth={2.4}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Order outcomes"
          subtitle="Payment and ticket validation status"
          isEmpty={orderStatusTotal === 0 && ticketStatusTotal === 0}
        >
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
            <MiniPieChart title="Payments" data={orderStatusBreakdown} colors={orderStatusColors} />
            <MiniPieChart title="Tickets" data={ticketStatusBreakdown} colors={ticketStatusColors} />
          </div>
        </ChartCard>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <ChartCard
          title="Tier revenue"
          subtitle="Revenue and sold tickets by ticket tier"
          isEmpty={tierChartData.length === 0}
        >
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={tierChartData} margin={{ top: 10, right: 14, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.18)" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} tickFormatter={(value) => String(value).slice(0, 14)} />
              <YAxis
                yAxisId="revenue"
                tickFormatter={(value) => compactNumber(Number(value))}
                tick={{ fontSize: 12 }}
                width={42}
              />
              <YAxis
                yAxisId="tickets"
                orientation="right"
                tickFormatter={(value) => compactNumber(Number(value))}
                tick={{ fontSize: 12 }}
                width={32}
              />
              <Tooltip formatter={eventChartTooltipFormatter} />
              <Legend />
              <Bar yAxisId="revenue" dataKey="revenue" name="Revenue" fill={chartColors.charcoal} radius={[6, 6, 0, 0]} />
              <Line
                yAxisId="tickets"
                type="monotone"
                dataKey="sold"
                name="Tickets sold"
                stroke={chartColors.purple}
                strokeWidth={2.4}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Inventory by tier"
          subtitle="Sold tickets compared with remaining allocation"
          isEmpty={tierChartData.length === 0}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={tierChartData} margin={{ top: 10, right: 14, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.18)" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} tickFormatter={(value) => String(value).slice(0, 14)} />
              <YAxis tickFormatter={(value) => compactNumber(Number(value))} tick={{ fontSize: 12 }} width={36} />
              <Tooltip formatter={eventChartTooltipFormatter} />
              <Legend />
              <Bar dataKey="sold" name="Sold" stackId="inventory" fill={chartColors.green} radius={[5, 5, 0, 0]} />
              <Bar dataKey="available" name="Available" stackId="inventory" fill={chartColors.gold} radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <div className="ego-surface overflow-hidden">
          <div className="border-b border-line px-6 py-5">
            <h2 className="text-lg font-bold text-foreground">Tier breakdown</h2>
            <p className="text-sm text-muted-foreground">Sales and revenue per ticket tier</p>
          </div>
          <div className="ego-divide">
            {tierStats.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                No ticket tiers configured.
              </div>
            ) : (
              tierStats.map((tier) => {
                const total = tier.quantity || tier.sold;
                const sellThrough = total > 0 ? Math.round((tier.sold / total) * 100) : 0;
                return (
                  <div key={tier.id || tier.name} className="px-6 py-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-foreground">{tier.name}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {formatCurrency(tier.price)} each · {tier.quantity} allocated
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-foreground">
                          {formatCurrency(tier.revenue)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {tier.sold} sold · {tier.available} left
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-muted">
                      <div
                        className="h-full rounded-full bg-[var(--pp-gold)]"
                        style={{ width: `${Math.min(sellThrough, 100)}%` }}
                      />
                    </div>
                    <div className="mt-1 text-right text-[11px] text-[#9aa3b2]">{sellThrough}%</div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="ego-surface overflow-hidden">
          <div className="border-b border-line px-6 py-5">
            <h2 className="text-lg font-bold text-foreground">Order pulse</h2>
            <p className="text-sm text-muted-foreground">Order outcomes for this screening</p>
          </div>
          <div className="grid grid-cols-2 gap-3 p-6">
            <Stat label="Total orders" value={stats.totalOrders} />
            <Stat label="Paid" value={stats.paidOrders} accent="text-[#047857]" />
            <Stat label="Pending" value={stats.pendingOrders} accent="text-[#b45309]" />
            <Stat label="Capacity" value={stats.capacity} />
          </div>
          {peakDay.date && (
            <div className="border-t border-line px-6 py-4 text-sm text-muted-foreground">
              Peak sales day{" "}
              <span className="font-semibold text-foreground">{formatCompactDate(peakDay.date)}</span>
              {" · "}
              <span className="font-semibold text-foreground">{formatCurrency(peakDay.revenue)}</span>
              {" · "}
              <span className="font-semibold text-foreground">
                {peakDay.count} ticket{peakDay.count === 1 ? "" : "s"}
              </span>
            </div>
          )}
        </div>
      </div>

      {canViewOrders && (
        <div className="ego-surface overflow-hidden">
          <div className="border-b border-line px-6 py-5">
            <h2 className="text-lg font-bold text-foreground">Recent orders</h2>
            <p className="text-sm text-muted-foreground">Latest buyer activity for this screening</p>
          </div>
          {recentOrders.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-surface-muted text-muted-foreground">
                <Receipt size={18} />
              </div>
              <p className="text-sm text-muted-foreground">No orders for this screening yet.</p>
            </div>
          ) : (
            <div className="ego-divide">
              {recentOrders.map((order) => {
                const tickets = order.tickets ?? [];
                const checkedIn = tickets.filter((ticket) => ticket.status === "checked-in").length;
                return (
                  <div
                    key={order.reference}
                    className="grid gap-3 px-6 py-4 transition hover:bg-surface-muted lg:grid-cols-[1.4fr_1fr_0.7fr_0.9fr] lg:items-center"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-foreground">
                        {order.customer?.name || "Legacy customer"}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {order.customer?.email || "—"}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className="ego-badge ego-badge-brand">{order.reference}</span>
                        <span className={paymentBadgeFor(order.paymentStatus)}>{order.paymentStatus}</span>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {tickets.length} ticket{tickets.length === 1 ? "" : "s"}
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className="ego-badge ego-badge-success">{checkedIn} in</span>
                        <span className="ego-badge ego-badge-neutral">
                          {tickets.length - checkedIn} pending
                        </span>
                      </div>
                    </div>
                    <div className="text-sm font-bold text-foreground">{formatCurrency(order.total)}</div>
                    <div className="text-xs text-muted-foreground">{formatDateTime(order.createdAt)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {canIssueTickets && (
        <IssueTicketsDialog
          event={event}
          isOpen={isIssueOpen}
          onClose={() => setIsIssueOpen(false)}
          onIssued={() => {
            void load();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="rounded-xl border border-line bg-surface-muted p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-xl font-bold tracking-tight ${accent ?? "text-foreground"}`}>
        {value}
      </div>
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  isEmpty,
  children,
}: {
  title: string;
  subtitle: string;
  isEmpty: boolean;
  children: ReactNode;
}) {
  return (
    <div className="ego-surface overflow-hidden">
      <div className="border-b border-line px-6 py-5">
        <h2 className="text-lg font-bold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="p-5">
        {isEmpty ? (
          <div className="flex h-[280px] items-center justify-center rounded-xl border border-dashed border-line bg-surface-muted px-6 text-center text-sm font-semibold text-muted-foreground">
            No analytics data for this screening yet.
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function MiniPieChart({
  title,
  data,
  colors,
}: {
  title: string;
  data: Array<{ status: string; count: number }>;
  colors: Record<string, string>;
}) {
  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="min-w-0">
      <div className="mb-2 text-sm font-bold text-foreground">{title}</div>
      {total === 0 ? (
        <div className="flex h-[150px] items-center justify-center rounded-xl border border-dashed border-line bg-surface-muted text-sm font-semibold text-muted-foreground">
          No data yet
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={data}
              dataKey="count"
              nameKey="status"
              innerRadius={42}
              outerRadius={68}
              paddingAngle={3}
            >
              {data.map((entry) => (
                <Cell key={entry.status} fill={colors[entry.status] ?? chartColors.slate} />
              ))}
            </Pie>
            <Tooltip formatter={eventChartTooltipFormatter} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function compactNumber(value: number) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function formatShortDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}

function eventChartTooltipFormatter(value: unknown, name: unknown) {
  const label = String(name);
  const numeric = Number(value);
  if (label.toLowerCase().includes("revenue")) {
    return [formatCurrency(Number.isFinite(numeric) ? numeric : 0), label];
  }
  return [Number.isFinite(numeric) ? compactNumber(numeric) : String(value), label];
}
